import { useState } from "react";

import type {
  AttributeReviewDraftSelection,
  AttributeReviewMappingRow,
} from "@/hooks/useAttributeReview";
import {
  useReviewPathActions,
  type DecideRows,
} from "@/hooks/useReviewPathActions";
import { useKindOptions } from "@/hooks/useKindOptions";
import { usePoolSearch } from "@/hooks/usePoolSearch";
import { fieldDescription } from "@/lib/attributeReview/fieldDescription";
import { kindWarning } from "@/lib/attributeReview/rowDecision";
import { stepOne } from "@/lib/attributeReview/stepOne";
import { isLinked } from "@/lib/attributeReview/pathDecision";
import { requiredWarning } from "@/lib/attributeReview/requiredWarning";
import {
  lostWordsNote,
  pickSentence,
  pickWhy,
} from "@/lib/attributeReview/pickWhy";
import { isApprovedAttributeReviewStatus } from "@/lib/attributeReview/reviewQueueStatus";
import { shownAnswer } from "@/lib/attributeReview/shownAnswer";
import { countDropped, countLooks } from "@/lib/attributeReview/valueLook";
import { pickTarget } from "@/lib/attributeReview/pickTarget";
import { saveSummary } from "@/lib/attributeReview/saveSummary";
import {
  noFieldTarget,
  pickedOf,
  saveTargetName,
} from "@/lib/attributeReview/noFieldPick";
import { usePathLabels } from "@/hooks/usePathLabels";
import { formatProductType } from "@/lib/formatProductType";
import {
  poolChoices,
  valueRow,
  type PoolValue,
} from "@/lib/attributeReview/valueView";
import type { Pool } from "../AttributeReviewRow/PoolSection";
import { useRowDecision } from "../AttributeReviewRow/useRowDecision";
import { IgnoreConfirm } from "../ReviewQueue/IgnoreConfirm";
import { DecisionStep } from "./DecisionStep";
import { ReviewDrawerFooter } from "./ReviewDrawerFooter";
import { ReviewDrawerHeader } from "./ReviewDrawerHeader";
import { SaveStep } from "./SaveStep";
import { ValuesStep } from "./ValuesStep";
import { useReviewDrawerSave } from "./useReviewDrawerSave";
import { RawJsonPanel } from "./RawJsonPanel";

export type ReviewDrawerBodyProps = {
  sameLeafLabel?: string;
  marketplace: string;
  productType: string;
  fieldLabelOf: (id: string) => string;
  row: AttributeReviewMappingRow;
  sessionId: string;
  draft?: AttributeReviewDraftSelection;
  positionText: string;
  isApproving: boolean;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
  afterSettleId: string | null;
  onOpenRow: (id: string | null) => void;
  onDraftChange: (
    id: string,
    draft: AttributeReviewDraftSelection | null,
  ) => void;
  approveRows: (
    rows: AttributeReviewMappingRow[],
  ) => Promise<{ ok: boolean; error: string | null }>;
  ignoreRows: (rows: AttributeReviewMappingRow[]) => Promise<boolean>;
  decideRows: DecideRows;
  onUnreject?: (row: AttributeReviewMappingRow) => void | Promise<boolean>;
};

export function ReviewDrawerBody({
  row,
  sessionId,
  draft,
  positionText,
  isApproving,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose,
  afterSettleId,
  onOpenRow,
  onDraftChange,
  approveRows,
  ignoreRows,
  decideRows,
  onUnreject,
  sameLeafLabel,
  marketplace,
  productType,
  fieldLabelOf,
}: ReviewDrawerBodyProps) {
  const id = String(row.id ?? "");
  const change = (next: AttributeReviewDraftSelection | null) =>
    onDraftChange(id, next);
  const decision = useRowDecision(draft, change);
  const paths = useReviewPathActions(row, decideRows);
  const poolSearch = usePoolSearch();
  const [unlocked, setUnlocked] = useState(false);
  const [nameFocus, setNameFocus] = useState(0);
  const readOnly =
    isApprovedAttributeReviewStatus(row.decisionStatus) && !unlocked;
  const shown = shownAnswer(row, draft);
  const pool = (readOnly ? row.pool : shown.pool) as Pool | null;
  const values = (readOnly ? row.values : shown.values) as PoolValue[];
  const hiddenCount =
    values.length === 0 ? ((row.values as unknown[] | null)?.length ?? 0) : 0;
  const fixes = readOnly ? {} : decision.fixes;
  const kinds = useKindOptions(sessionId);
  const { pickedPath, pickedLeaf, noFieldKind } = pickedOf(row, draft, (k) =>
    kinds.some((o) => o.kind === k && !o.productField),
  );
  const labelOf = usePathLabels(Boolean(pickedPath));
  const { pickLabel, toName } = pickTarget(
    row,
    pickedPath,
    pickedLeaf,
    labelOf,
  );
  const choices = poolChoices(pool?.values ?? [], values, fixes);
  const needLook = readOnly ? 0 : countLooks(values, fixes, choices);
  const linked = isLinked(row);
  const linkMode = linked && !readOnly && pickedPath === row.path;
  const canApprove = Boolean(
    pickedLeaf || pickedPath || noFieldKind || row.hidden,
  );

  const save = useReviewDrawerSave({
    row,
    otherBusy: paths.busy !== null || isApproving,
    readOnly,
    canApprove,
    afterSettleId,
    onOpenRow,
    approveRows,
    ignoreRows,
  });
  const { busy, saveError, confirmIgnore, locked } = save;
  const warning = requiredWarning(row.requirementLevel);
  const required = Boolean(warning?.required);
  const askIgnore = () =>
    warning ? save.setConfirmIgnore(true) : void save.ignore();

  const label = String(row.sourceAttribute ?? id);
  const store = formatProductType(marketplace);
  const why = pickWhy(row, required);
  const lead = readOnly ? null : pickSentence(row);
  const lostWords = readOnly ? "" : lostWordsNote(countDropped(values, fixes));
  const valuesUnmapped =
    values.length === 0 && Number(row.sourceEnumValueCount ?? 0) > 0;
  const one = stepOne({
    row,
    readOnly,
    pick: {
      path: pickedPath,
      label: noFieldKind ? noFieldTarget(noFieldKind, store) : pickLabel,
    },
    pickedLeaf,
    noField: Boolean(noFieldKind),
    needLook,
    sameLeafLabel,
    lead,
    lostWords,
    valuesUnmapped,
    kindHint: kindWarning(
      (row.answers as { kind?: string | null }[] | null) ?? [],
      decision.kind ?? (row.kind as string | null) ?? null,
      kinds.map((option) => option.kind),
    ),
  });
  const summary = saveSummary({
    store: store,
    field: label,
    targetName: saveTargetName(noFieldKind, store, {
      picked: Boolean(pickedPath || pickedLeaf),
      linkMode,
      toName,
      label: pickLabel,
    }),
    link: readOnly ? linked : linkMode,
    total: values.length,
    ignored: values.filter(
      (v) => valueRow(v, fixes[v.store_value], choices).ignored,
    ).length,
    needLook,
    valuesUnmapped,
  });
  return (
    <>
      <ReviewDrawerHeader
        positionText={positionText}
        label={label}
        description={fieldDescription(row)}
        requirementLevel={row.requirementLevel}
        storeLine={`${store} › ${formatProductType(productType)}`}
        hasPrev={hasPrev}
        hasNext={hasNext}
        onPrev={onPrev}
        onNext={onNext}
        onClose={onClose}
      />
      <div className="flex-1 space-y-3.5 overflow-y-auto p-[18px]">
        <div className="flex flex-col">
          <DecisionStep
            row={row}
            pickedPath={pickedPath}
            pickedLeaf={pickedLeaf}
            noFieldKind={noFieldKind}
            marketplace={marketplace}
            mark={one.marks[0]}
            status={one.tag}
            proof={one.proof}
            notes={one.notes}
            readOnly={readOnly}
            linked={linked}
            linkMode={linkMode}
            fieldLabelOf={fieldLabelOf}
            decision={decision}
            onCopy={
              readOnly
                ? undefined
                : (copyOf) => void save.settle(() => paths.copy(copyOf))
            }
            onUnlink={() => void paths.unlink()}
            onEdit={() =>
              onUnreject ? void onUnreject(row) : setUnlocked(true)
            }
            onIgnoreCopy={askIgnore}
            locked={locked}
            sessionId={sessionId}
            nameFocus={nameFocus}
            why={why}
          />
          <ValuesStep
            row={row}
            draft={draft}
            readOnly={readOnly}
            pool={pool}
            values={values}
            fixes={fixes}
            decision={decision}
            poolSearch={poolSearch}
            hiddenCount={hiddenCount}
            pickedPath={pickedPath}
            pickedLeaf={pickedLeaf}
            noField={Boolean(noFieldKind)}
            sessionId={sessionId}
            marketplace={marketplace}
            mark={one.marks[1]}
            needLook={needLook}
          />
          <SaveStep
            mark={saveError ? "err" : one.marks[2]}
            summary={summary}
            saveError={saveError}
            showActions={!readOnly}
            linkMode={linkMode}
            required={required}
            row={row}
            canApprove={canApprove}
            working={locked}
            approving={busy === "approve" || isApproving}
            onApprove={save.approve}
            onIgnore={askIgnore}
            onNotSame={() => setNameFocus((n) => n + 1)}
            onShowToSeller={() => void paths.showToSeller()}
            onUndoIgnore={() => void onUnreject?.(row)}
          />
        </div>
        <RawJsonPanel row={row} store={store} />
      </div>
      <ReviewDrawerFooter
        hasPrev={hasPrev}
        hasNext={hasNext}
        onPrev={onPrev}
        onNext={onNext}
        showActions={!readOnly}
        required={required}
        hidden={Boolean(row.hidden)}
        linkMode={linkMode}
      />
      <IgnoreConfirm
        row={confirmIgnore ? row : null}
        onCancel={() => save.setConfirmIgnore(false)}
        onConfirm={() => void save.ignore()}
      />
    </>
  );
}
